/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2025 Rochester Software Associates (service@rocsoft.com)
 */

import PropTypes from 'prop-types';

import useCallbackRef from '~/utils/hooks/useCallbackRef';

import Button from '../buttons/Button';
import { getAutomationID } from '../id/AutomationID';
import { buildOrderingID } from '../id/RootAutomationID';
import LocalizedString from '../text/LocalizedString';
import useLocalizationTextFormatted from '../text/useLocalizationTextFormatted';
import Tooltip from '../tooltip/Tooltip';

import { AlertType } from './AlertType';

const typeToColor = new Map();
typeToColor.set('muted', 'muted');
typeToColor.set(AlertType.INFORMATION, 'info');
typeToColor.set(AlertType.SUCCESS, 'success');
typeToColor.set(AlertType.USER_ERROR, 'warning');
typeToColor.set(AlertType.SYSTEM_ERROR, 'danger');

const Toast = ({ type, onDismiss, children, fading }) => {
    const rootID = buildOrderingID('common');
    const automationID = getAutomationID(rootID, 'closeToasts', type);
    const [tooltipRef, setTooltipRef] = useCallbackRef();
    const color = typeToColor.get(type);
    const ariaLabel = useLocalizationTextFormatted('COMMON_UI.ARIA.TOAST_CLOSE_BUTTON', { MESSAGE_TYPE: color });
    const classes = ['rsa-toast', `rsa-toast-${color}`];
    if (fading) {
        classes.push('rsa-toast-fading');
    }
    return (
        // The alert role will force the screen reader to announce the text of the toast even if it is not in focus
        <div role="alert" className="rsa-toast-container">
            <div className={classes.join(' ')}>
                <div className="rsa-toast-body">{children}</div>
                <Button
                    inline
                    id={automationID.id}
                    className="rsa-toast-close-all"
                    action={onDismiss}
                    ref={setTooltipRef}
                    ariaLabel={ariaLabel}
                >
                    <LocalizedString localeKey="COMMON_UI.BUTTON.TOAST_CLOSE" />
                </Button>
                <Tooltip target={tooltipRef}>
                    <LocalizedString
                        localeKey="COMMON_UI.LABEL.TOAST_CLOSE_TOOLTIP"
                        parameters={{ MESSAGE_TYPE: color }}
                    />
                </Tooltip>
            </div>
        </div>
    );
};

Toast.propTypes = {
    type: PropTypes.oneOf([AlertType.INFORMATION, AlertType.SUCCESS, AlertType.USER_ERROR, AlertType.SYSTEM_ERROR]).isRequired,
    onDismiss: PropTypes.func,
    children: PropTypes.node.isRequired,
    fading: PropTypes.bool,
};

export default Toast;
